/**
 * 微专栏数据看板埋点常量（v5）
 *
 * 严格对齐三处（任一处变更必须同步另外两处）：
 * - backend：apps/meikan-backend/egg/app/service/postView.js
 *            apps/meikan-backend/egg/app/controller/postView.js
 * - App    ：apps/meikanAppNew/src/constants/tracker.ts
 * - H5     ：apps/meikan-mobile/src/constants/tracker.ts
 *
 * 这里只放跨调用方共享的字面量常量，不放节流缓存等运行时状态。
 * 抽出常量的目的是消除"字符串字面量笔误"，不引入跨仓 SDK
 * （见 memory/project_microcolumn_v5_followup.md tracker 三端收敛讨论结论）。
 */

export const TRACK_API = "/api/post/view/track";

export const VIEW_TYPES = ["expose", "detail", "click_origin"] as const;
export type ViewType = (typeof VIEW_TYPES)[number];

// Web 端 client 标识固定值（与后端 ALLOWED_CLIENTS 对齐：app / web / h5）。
// 抽常量是为了避免在 utils/tracker.ts 里硬编码 'web' 字面量造成跨端串号。
export const CLIENT = "web" as const;

/**
 * inboundChannel 当前 Web 实装的核心枚举 + 为三端对齐保留的跨端共享字面量。
 *
 * 注意：后端不强制 enum（v5 §3.4），允许运营动态加 promo_xxx 推广渠道，
 * 因此这里只列出当前需要在前端字面量中心化的渠道。WEIBO / QZONE 等 Web 未实装
 * 的渠道也保留，避免三端常量漂移引入笔误；调用方传 string 也合法。
 */
export const INBOUND_CHANNELS = {
  WECHAT_FRIEND: "wechat_friend",
  WECHAT_TIMELINE: "wechat_timeline",
  WECHAT_FAVORITE: "wechat_favorite",
  COPY_LINK: "copy_link",
  POSTER: "poster",
  QRCODE: "qrcode",
  // 微博 / QQ 空间：H5 端在微信内置浏览器 "…" 菜单里通过 wx.onMenuShareWeibo /
  // onMenuShareQZone 注入；Web 端目前没有这两个分享入口（保留枚举值以便后端聚合
  // 时能识别字面量并避免三端常量漂移）。
  WEIBO: "weibo",
  QZONE: "qzone",
  DIRECT: "direct",
} as const;
export type CoreInboundChannel = (typeof INBOUND_CHANNELS)[keyof typeof INBOUND_CHANNELS];

// 客户端节流（2026-06-10 PM 调整：原 5 分钟时间戳 → 按自然日去重）：
// 同 (post, viewType) 在同一自然日（北京时间）内只发一次。与服务端反爬 1 次/天
// 完全对齐，避免"客户端发了但服务端丢"的无效请求。
// 与 App in-memory / H5 sessionStorage 口径一致，三端共用 currentDateKey() yyyymmdd 字符串。
export function currentDateKey(): string {
  // 服务端按北京时间 0 点切自然日（dayjs().startOf('day') 在 UTC+8 服务器上）。
  // 客户端跨时区设备如果按 UTC 切，会在北京时间 23 点 / 7 点附近出现"客户端已切但
  // 服务端没切"的边界 mismatch。统一按北京时间算 yyyymmdd 让两端口径完全对齐。
  const now = new Date();
  const bj = new Date(now.getTime() + 8 * 3600000);
  const y = bj.getUTCFullYear();
  const m = String(bj.getUTCMonth() + 1).padStart(2, "0");
  const d = String(bj.getUTCDate()).padStart(2, "0");
  return `${y}${m}${d}`;
}

// 服务端反爬阈值（每 user|did/post/viewType/client 每天上限）。
// 2026-06-10 PM 调整：5 次/天 → 1 次/天。Web 不直接消费，仅与 backend 对齐文档化。
export const TRACK_DAILY_LIMIT_SERVER = 1;

// localStorage key：Web 端持久化 did。
// 与 H5 故意同名（"mk_did"），但运行域不同（h5 / web 独立 origin），不存在跨域读写。
export const DID_STORAGE_KEY = "mk_did";

// sessionStorage 节流键前缀，避免与业务用户态 key 冲突。
export const TRACK_THROTTLE_PREFIX = "mk_track:";
